import { useEffect } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { createNadDebug } from '../../utils/debugLog'
import { hasApiKeysAdmin, isAdminLocalDev } from './adminAccess'

export { hasApiKeysAdmin } from './adminAccess'

// NAD trace for the guard decision (feature=admin-api-keys). Without it, "why
// can't I reach /admin/api" is untraceable — the branch is security-relevant and
// silently redirects.
const guardLog = createNadDebug('dw:admin-api-keys:guard')

// The API-key admin surface (/admin/api) is gated on the service-role-only
// GoTrue app_metadata.role === 'admin' claim. The backend is the real boundary —
// this just hides the route so non-admins never see it. Local dev bypasses, like
// AdminTopupRoute / BannerAdminRoute / ProtectedRoute.
//
// Reads app_metadata ONLY — user_metadata is client-editable and must never gate
// an admin affordance. The claim is baked into the JWT at issue time, so a freshly
// granted admin only sees the route after a token refresh / re-login (accepted:
// the backend stays authoritative until then).
//
// NOTE ON THE BACKEND BOUNDARY: unlike /admin/banners, the endpoints this page
// calls (POST/GET/DELETE /auth/api-keys) are gated by `require_active_user`, not
// `require_admin` — they are per-user key management, not an admin API. A
// non-admin who bypasses this guard can therefore still manage THEIR OWN keys,
// which is exactly what the endpoints have always allowed. The admin gate here is
// about surfacing the tool, not about privilege.
export default function ApiKeysAdminRoute() {
  const { user, isLoading } = useAuth()
  const hasClaim = hasApiKeysAdmin(user)
  const localDevBypass = isAdminLocalDev()
  const allowed = localDevBypass || hasClaim
  // `reason` distinguishes a real claim from the dev bypass, which is the
  // difference that matters when reading a trace.
  const reason = hasClaim ? 'claim' : localDevBypass ? 'local_dev_bypass' : 'denied'

  useEffect(() => {
    if (isLoading) return
    guardLog({
      event: 'admin-api-keys:guard',
      feature: 'admin-api-keys',
      concern: 'guard',
      allowed,
      reason,
    })
  }, [isLoading, allowed, reason])

  if (isLoading) return null
  if (allowed) return <Outlet />
  return <Navigate to="/dashboard" replace />
}
